import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, MapPin, Layers, Lock, Inbox, Send, Map as MapIcon,
  Plus, ArrowRight, BadgeCheck, LogOut, User, Sparkles, FileUp, FileSignature,
  BookOpen, Smartphone, Monitor
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getMyDashboard, authLogout, authFetch } from "@/lib/api";
import AccessRequestsSection from "@/components/AccessRequestsSection";
import PWAInstallButton from "@/components/PWAInstallButton";
import SanitaryAlertsBell from "@/components/SanitaryAlertsBell";
import QuickEntrySection from "@/components/QuickEntrySection";
import ImportGeneralIAModal from "@/components/ImportGeneralIAModal";
import { usePendingLockRequests, PendingBadge } from "@/hooks/usePendingLockRequests";
import ImportDraftsModal from "@/components/ImportDraftsModal";
import { ConventionReminder } from "@/components/MyConventionSection";

const COL = {
  bg: "#F9F8F6",
  ink: "#1A1A1A",
  sub: "#4A4A4A",
  line: "#0000000D",
  green: "#2D6A4F",
  greenSoft: "#2D6A4F12",
  orange: "#E07A5F",
  orangeSoft: "#E07A5F12",
  gold: "#D4A373",
  yellow: "#E9C46A",
};

const Stat = ({ icon: Icon, label, value, color, testId, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="text-left p-5 rounded-2xl border bg-white transition-all hover:-translate-y-0.5 hover:shadow-md w-full"
    style={{ borderColor: COL.line }}
  >
    <div className="flex items-center gap-2 mb-3">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color + "18" }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </span>
      <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: COL.sub }}>
        {label}
      </span>
    </div>
    <p className="text-3xl font-bold tracking-tight" style={{ color: COL.ink, fontFamily: "Manrope, sans-serif" }}>
      {value}
    </p>
  </button>
);

const ParcelRow = ({ parcel, badge, onOpen }) => (
  <button
    onClick={() => onOpen(parcel)}
    data-testid={`dashboard-parcel-${parcel.parcel_id}`}
    className="w-full text-left p-4 rounded-xl border bg-white hover:border-[#2D6A4F]/40 hover:shadow-sm transition-all flex items-center gap-3"
    style={{ borderColor: COL.line }}
  >
    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: COL.greenSoft }}>
      <MapPin className="w-4 h-4" style={{ color: COL.green }} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold truncate" style={{ color: COL.ink }}>
          {parcel.name || parcel.parcel_id}
        </p>
        {badge}
      </div>
      <p className="text-[11px] mt-0.5 truncate" style={{ color: COL.sub }}>
        <span className="font-mono">{parcel.parcel_id}</span>
        {" · "}{parcel.commune || "—"}{" · "}{parcel.surface_ha || 0} ha
      </p>
    </div>
    <ArrowRight className="w-4 h-4 shrink-0" style={{ color: COL.sub }} />
  </button>
);

const Section = ({ title, subtitle, action, children, testId }) => (
  <section className="space-y-4" data-testid={testId}>
    <div className="flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-xl font-bold" style={{ color: COL.ink, fontFamily: "Manrope, sans-serif" }}>
          {title}
        </h2>
        {subtitle && <p className="text-sm mt-0.5" style={{ color: COL.sub }}>{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImportIA, setShowImportIA] = useState(false);
  const [showImportDrafts, setShowImportDrafts] = useState(false);
  const [importDraftsCount, setImportDraftsCount] = useState(0);
  const [focusDraftId, setFocusDraftId] = useState(null);
  const pendingLocks = usePendingLockRequests(!!data?.user?.is_admin);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyDashboard();
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError("Impossible de charger le tableau de bord.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Compteur de brouillons IA à valider (badge)
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + "/api";
        const r = await authFetch(`${API}/import/drafts/count`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setImportDraftsCount(d.count || 0);
      } catch (_e) { /* silencieux */ }
    };
    fetchCount();
    const it = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(it); };
  }, []);

  const handleLogout = async () => {
    try { await authLogout(); } catch {}
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COL.bg }} data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COL.green }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COL.bg }}>
        <div className="text-center space-y-3">
          <p style={{ color: COL.sub }}>{error || "Erreur inconnue"}</p>
          <Button onClick={() => window.location.reload()} data-testid="dashboard-retry">Réessayer</Button>
        </div>
      </div>
    );
  }

  const { user, subscription, followed_parcels = [], manual_parcels = [], locked_parcels = [], access_requests = { sent_pending: 0, received_pending: 0 }, stats = {} } = data;
  const subscriptionLabel = subscription
    ? (subscription.plan_name || subscription.status || "Actif")
    : "Aucun abonnement";

  const openParcel = (p) => navigate(`/carnet/${encodeURIComponent(p.parcel_id)}`);

  return (
    <div className="min-h-screen" style={{ background: COL.bg }} data-testid="dashboard-page">
      {/* Top bar */}
      <header
        className="sticky top-safe z-40 backdrop-blur-md border-b"
        style={{ background: "rgba(249,248,246,0.85)", borderColor: COL.line }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-5 py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-3 shrink-0">
            <img src="/veyer-ciel-logo.png" alt="Véyèr CIEL" className="h-9 shrink-0" />
            <span className="hidden sm:inline text-xs px-2 py-1 rounded-full font-medium" style={{ background: COL.greenSoft, color: COL.green }}>
              Tableau de bord
            </span>
          </div>
          {/* Sur téléphone, les boutons n'affichent que leur icône pour tenir sur une ligne */}
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <Button
              data-testid="dashboard-goto-map"
              variant="outline"
              className="h-9 px-3 sm:px-4 rounded-full text-xs gap-2"
              onClick={() => navigate("/map")}
            >
              <MapIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Voir la carte</span>
            </Button>
            <Button
              data-testid="dashboard-goto-account"
              variant="outline"
              className="h-9 px-3 sm:px-4 rounded-full text-xs gap-2"
              onClick={() => navigate("/account")}
            >
              <User className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Mon compte</span>
            </Button>
            <PWAInstallButton
              variant="outline"
              className="h-9 px-3 sm:px-4 rounded-full text-xs gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              label="Installer l'app"
              labelClassName="hidden sm:inline"
              testId="dashboard-pwa-install"
            />
            <SanitaryAlertsBell />
            {user.is_coordinator && !user.is_admin && (
              <Button
                data-testid="dashboard-goto-conventions"
                className="h-9 px-3 sm:px-4 rounded-full text-xs gap-2"
                style={{ background: "#0E7490", color: "white" }}
                onClick={() => navigate("/conventions")}
              >
                <FileSignature className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Conventions</span>
              </Button>
            )}
            {user.is_admin && (
              <Button
                data-testid="dashboard-goto-admin"
                className="h-9 px-3 sm:px-4 rounded-full text-xs gap-2"
                style={{ background: COL.orange, color: "white" }}
                onClick={() => navigate("/admin-dashboard")}
              >
                <Sparkles className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin</span>
                <PendingBadge count={pendingLocks} />
              </Button>
            )}
            <Button
              data-testid="dashboard-logout"
              variant="ghost"
              className="h-9 px-3 sm:px-4 rounded-full text-xs gap-2"
              onClick={handleLogout}
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8 space-y-10">
        {/* Hero */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest" style={{ color: COL.sub }}>
              Bienvenue
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold mt-1" style={{ color: COL.ink, fontFamily: "Manrope, sans-serif" }}>
              {user.name || user.email}
            </h1>
            <div className="flex items-center gap-2 mt-3">
              {subscription ? (
                <Badge className="gap-1" style={{ background: COL.greenSoft, color: COL.green, border: "none" }}>
                  <BadgeCheck className="w-3 h-3" /> {subscriptionLabel}
                </Badge>
              ) : (
                <Badge variant="outline" style={{ color: COL.sub }}>
                  Aucun abonnement actif
                </Badge>
              )}
              {user.is_admin && (
                <Badge style={{ background: COL.orangeSoft, color: COL.orange, border: "none" }}>
                  Administrateur
                </Badge>
              )}
              {user.is_coordinator && (
                <Badge style={{ background: "#0891B218", color: "#0E7490", border: "none" }}>
                  Coordinateur
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Convention à signer (producteurs) */}
        {!user.is_admin && !user.is_coordinator && <ConventionReminder />}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat
            icon={Layers}
            label="Parcelles suivies"
            value={stats.followed_count}
            color={COL.green}
            testId="stat-followed"
            onClick={() => navigate("/parcelles")}
          />
          <Stat
            icon={MapPin}
            label="Mes parcelles"
            value={stats.manual_count}
            color={COL.orange}
            testId="stat-manual"
            onClick={() => navigate("/map")}
          />
          <Stat
            icon={Lock}
            label="Verrouillées"
            value={stats.locked_count}
            color={COL.gold}
            testId="stat-locked"
            onClick={() => document.getElementById("locked-section")?.scrollIntoView({ behavior: "smooth" })}
          />
          <Stat
            icon={Inbox}
            label="Demandes d'accès"
            value={access_requests.received_pending}
            color={COL.yellow}
            testId="stat-requests"
          />
        </div>

        {/* Saisie rapide registre phyto — agriculteurs pressés */}
        <QuickEntrySection />

        {/* Quick actions */}
        <div className="grid sm:grid-cols-3 gap-3">
          <Button
            data-testid="action-map"
            onClick={() => navigate("/map")}
            className="h-14 rounded-2xl text-sm gap-2 justify-start px-4"
            style={{ background: COL.green, color: "white" }}
          >
            <MapIcon className="w-4 h-4" /> Voir la carte
          </Button>
          <Button
            data-testid="action-import-ia"
            onClick={() => setShowImportIA(true)}
            className="h-14 rounded-2xl text-sm gap-2 justify-start px-4 text-white relative"
            style={{ background: "linear-gradient(135deg, #7C3AED, #5B21B6)" }}
          >
            <FileUp className="w-4 h-4" />
            <span className="flex-1 text-left">Import IA (facture, sol, traitement)</span>
            {importDraftsCount > 0 && (
              <span
                role="button"
                tabIndex={0}
                data-testid="import-drafts-badge"
                onClick={(e) => { e.stopPropagation(); setFocusDraftId(null); setShowImportDrafts(true); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setFocusDraftId(null); setShowImportDrafts(true); } }}
                className="text-[10px] px-1.5 py-0.5 rounded-full font-bold cursor-pointer"
                style={{ background: "#FBBF24", color: "#78350F" }}
                title="Brouillons IA à valider"
              >
                {importDraftsCount} à valider
              </span>
            )}
          </Button>
          <Button
            data-testid="action-add"
            onClick={() => navigate("/map?add=1")}
            className="h-14 rounded-2xl text-sm gap-2 justify-start px-4 bg-white"
            variant="outline"
            style={{ color: COL.ink, borderColor: COL.line }}
          >
            <Plus className="w-4 h-4" /> Ajouter une parcelle manuelle
          </Button>
        </div>

        {/* Guide d'utilisation (PDF statiques dans public/guides) */}
        <div className="rounded-2xl p-4 flex items-center gap-3 flex-wrap bg-white" style={{ border: `1px solid ${COL.line}` }} data-testid="user-guide">
          <BookOpen className="w-6 h-6 shrink-0" style={{ color: COL.green }} />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-bold" style={{ color: COL.ink }}>Guide d'utilisation</p>
            <p className="text-xs" style={{ color: COL.sub }}>Toutes les actions de l'application pas à pas, avec une capture d'écran pour chaque étape.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button asChild variant="outline" className="h-9 rounded-full text-xs gap-2" data-testid="guide-phone">
              <a href="/guides/guide-veyer-ciel-telephone.pdf" target="_blank" rel="noopener noreferrer"><Smartphone className="w-3.5 h-3.5" /> Version téléphone</a>
            </Button>
            <Button asChild variant="outline" className="h-9 rounded-full text-xs gap-2" data-testid="guide-desktop">
              <a href="/guides/guide-veyer-ciel-ordinateur.pdf" target="_blank" rel="noopener noreferrer"><Monitor className="w-3.5 h-3.5" /> Version ordinateur</a>
            </Button>
          </div>
        </div>

        {/* Followed parcels */}
        <div id="followed-section">
          <Section
            title="Mes parcelles suivies"
            subtitle={followed_parcels.length === 0
              ? "Vous n'avez encore suivi aucune parcelle. Ouvrez la carte pour en sélectionner une."
              : `${followed_parcels.length} parcelle${followed_parcels.length > 1 ? "s" : ""} · ${stats.total_ha} ha au total`
            }
            testId="section-followed"
            action={
              <Button
                variant="ghost"
                className="text-xs gap-1"
                onClick={() => navigate("/parcelles")}
                data-testid="followed-see-all"
              >
                Voir toutes <ArrowRight className="w-3 h-3" />
              </Button>
            }
          >
            {followed_parcels.length === 0 ? (
              <div
                className="p-8 rounded-2xl border-2 border-dashed text-center"
                style={{ borderColor: COL.line }}
                data-testid="followed-empty"
              >
                <Layers className="w-8 h-8 mx-auto mb-3" style={{ color: COL.sub }} />
                <p className="text-sm" style={{ color: COL.sub }}>
                  Aucune parcelle suivie pour l'instant.
                </p>
                <Button
                  className="mt-4 rounded-full text-xs"
                  onClick={() => navigate("/map")}
                  style={{ background: COL.green, color: "white" }}
                  data-testid="followed-empty-cta"
                >
                  Explorer la carte
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {followed_parcels.slice(0, 8).map((p) => (
                  <ParcelRow
                    key={p.parcel_id}
                    parcel={p}
                    onOpen={openParcel}
                    badge={p.locked_by ? (
                      <Badge className="text-[9px] gap-1" style={{ background: COL.gold + "20", color: COL.gold, border: "none" }}>
                        <Lock className="w-2.5 h-2.5" /> Verrouillée
                      </Badge>
                    ) : null}
                  />
                ))}
                {followed_parcels.length > 8 && (
                  <p className="col-span-full text-center text-xs pt-2" style={{ color: COL.sub }}>
                    + {followed_parcels.length - 8} autres parcelles
                  </p>
                )}
              </div>
            )}
          </Section>
        </div>

        {/* Locked parcels */}
        <div id="locked-section">
          <Section
            title="Parcelles que j'ai verrouillées"
            subtitle="Accès exclusif au Carnet PRO. Vous pouvez partager l'accès avec jusqu'à 3 personnes gratuitement."
            testId="section-locked"
          >
            {locked_parcels.length === 0 ? (
              <div
                className="p-8 rounded-2xl border-2 border-dashed text-center"
                style={{ borderColor: COL.line }}
                data-testid="locked-empty"
              >
                <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: COL.sub }} />
                <p className="text-sm" style={{ color: COL.sub }}>
                  Vous n'avez verrouillé aucune parcelle.
                </p>
                <p className="text-xs mt-2" style={{ color: COL.sub }}>
                  Verrouillez une parcelle depuis la carte pour devenir propriétaire de son Carnet PRO.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {locked_parcels.map((p) => (
                  <ParcelRow
                    key={p.parcel_id}
                    parcel={p}
                    onOpen={openParcel}
                    badge={
                      <Badge className="text-[9px] gap-1" style={{ background: COL.gold + "20", color: COL.gold, border: "none" }}>
                        <Lock className="w-2.5 h-2.5" /> Propriétaire
                      </Badge>
                    }
                  />
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Access requests — Phase 4 */}
        <AccessRequestsSection />

        {/* Footer */}
        <footer className="pt-6 border-t flex items-center justify-between text-xs" style={{ borderColor: COL.line, color: COL.sub }}>
          <span>Véyèr CIEL by Biomyr</span>
          <span className="font-mono">v3.0 · IDs stables</span>
        </footer>
      </main>

      {showImportIA && (
        <ImportGeneralIAModal
          onClose={() => setShowImportIA(false)}
          onDraftCreated={(draft, opts) => {
            setImportDraftsCount((c) => c + 1);
            if (opts?.openValidation) {
              setFocusDraftId(draft.draft_id);
              setShowImportDrafts(true);
            }
          }}
        />
      )}

      {showImportDrafts && (
        <ImportDraftsModal
          onClose={() => { setShowImportDrafts(false); setFocusDraftId(null); }}
          focusDraftId={focusDraftId}
          onValidated={async () => {
            try {
              const API = process.env.REACT_APP_BACKEND_URL + "/api";
              const r = await authFetch(`${API}/import/drafts/count`);
              if (r.ok) { const d = await r.json(); setImportDraftsCount(d.count || 0); }
            } catch { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}
